import { QueryOptions } from '@shared/fetch-utils';
import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { buildUrl } from '@shared/utils';
import { useMPEFetch, useMutateMPEFetch } from '@retail/app/hooks';
import { ItemsCodeLists, MonitoringAssortmentItemDTO } from '@retail/monitoring-assortment/types';
import { PaginationResultDTO } from '@shared/fetch-utils';
import { AssortmentDTO } from '@retail/my-assortment/types';
import { mpeApi } from '@retail/mgpris/config';

export const getMgAssortmentsQueryKey = (mgItemNumbers: number[] = []) => [
  'monitoring-assortments',
  mgItemNumbers,
];

interface SearchParams {
  assortmentId: number;
  competitorStoreIds: number[];
  page?: number;
  size?: number;
  nCmpPrices?: number;
  supplierIds?: number[];
  groupNumbers?: string[];
  discountGroupIds?: number[];
  query?: string;
}

export function useFetchMonitoringAssortmentItems(
  {
    assortmentId,
    competitorStoreIds,
    page,
    size,
    nCmpPrices,
    supplierIds,
    groupNumbers,
    discountGroupIds,
    query,
  }: SearchParams,
  queryOptions: QueryOptions = {}
) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<PaginationResultDTO<MonitoringAssortmentItemDTO[]>>({
    method: 'GET',
    queryKey: [
      'monitoring-assortment',
      assortmentId,
      page,
      size,
      query,
      supplierIds,
      groupNumbers,
      discountGroupIds,
    ],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.monitoringAssortments.assortment.items.getFullLinkPath({ assortmentId }),
      searchParams: {
        storeIds: competitorStoreIds,
        page,
        size,
        nCmpPrices,
        groupNumbers,
        discountGroupIds,
        query,
        supplierIds,
      },
    }),
    ...queryOptions,
  });
}

export function useMutateMonitoringAssortmentItems() {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<PaginationResultDTO<MonitoringAssortmentItemDTO[]>, void, SearchParams>({
    method: 'GET',
    token,
    url: ({
      assortmentId,
      competitorStoreIds,
      page,
      size,
      nCmpPrices,
      supplierIds,
      groupNumbers,
      discountGroupIds,
      query,
    }) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.monitoringAssortments.assortment.items.getFullLinkPath({ assortmentId }),
        searchParams: {
          storeIds: competitorStoreIds,
          page,
          size,
          nCmpPrices,
          groupNumbers,
          discountGroupIds,
          query,
          supplierIds,
        },
      }),
  });
}

interface CreateAssortmentDTO {
  orgUnitId: number;
  title: string;
  itemCodes: ItemsCodeLists;
}

export function useCreateMonitoringAssortment() {
  const { token } = useAuthContext();
  const { mpe } = useMgPrisConfig();

  return useMutateMPEFetch<AssortmentDTO, CreateAssortmentDTO>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.monitoringAssortments.getFullLinkPath(),
    }),
    method: 'POST',
    token,
  });
}

export function useValidateImportedItems() {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();
  return useMutateMPEFetch<ItemsCodeLists, ItemsCodeLists>({
    method: 'POST',
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.monitoringAssortments.validateItemCodes.getFullLinkPath(),
    }),
  });
}

interface PostProps {
  mgItemNumbers: number[];
  finfoNumbers?: number[];
  gtinCodes?: number[];
}

interface PatchProps {
  title: string;
}

export const usePatchTitleOrItemsOfMonitoringAssortment = () => {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();
  return useMutateMPEFetch<object, PatchProps>({
    url: (assortmentId: number) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.monitoringAssortments.assortment.getFullLinkPath({ assortmentId }),
      }),
    method: 'PATCH',
    token,
  });
};

export const useDeleteMonitoringAssortment = () => {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void>({
    url: (assortmentId: number) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.monitoringAssortments.assortment.getFullLinkPath({ assortmentId }),
      }),
    method: 'DELETE',
    token,
  });
};

export const useAddItemsToMonitoringAssortment = () => {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<AssortmentDTO, PostProps>({
    url: (assortmentId: number) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.monitoringAssortments.assortment.items.getFullLinkPath({ assortmentId }),
      }),
    method: 'POST',
    token,
  });
};

export const useDeleteItemsFromMonitoringAssortment = () => {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void>({
    url: (assortmentId: number) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.monitoringAssortments.assortment.items.getFullLinkPath({ assortmentId }),
      }),
    method: 'DELETE',
    token,
  });
};
